/**
 * typesOperationService.ts
 *
 * Gestion dynamique des types d'opérations comptables — désormais BACKED BY API.
 *
 * Migration de localStorage → BD (table sc_metier.ecom_types_operation, exposée
 * via /secteurs/types-operation/*). Toutes les méthodes mutables passent par
 * le backend ; les méthodes de lecture utilisent un cache mémoire process-wide
 * pour éviter de spammer l'API.
 *
 * Compatibilité ascendante : pour les anciens consommateurs synchrones qui
 * appelaient `typesOperationService.getAll()` directement, on retourne le
 * cache (potentiellement vide au boot). Préférez `useTypesOperation()` (hook
 * React qui re-render quand les données arrivent), ou `await getAllAsync()`.
 */

import axiosInstance from './axios.config';
import { invaliderCatalogueProcessus } from './demandes-client.service';

/**
 * Mai 2026 — MM-10
 * Sens financier d'un type d'opération. Source de vérité = champ `sens`
 * en BD (table sc_metier.ecom_types_operation).
 */
export type SensOperation = 'DECAISSEMENT' | 'ENCAISSEMENT' | 'MIXTE';

/**
 * Définition d'une pièce à fournir, paramétrée par type d'opération.
 * Stockée dans le champ JSONB `piecesRequises` de ecom_types_operation.
 */
export interface PieceRequise {
  /** Identifiant technique unique au sein du type (ex: 'cni_passeport') */
  code: string;
  /** Libellé affiché à l'utilisateur */
  label: string;
  /** Pièce obligatoire pour la soumission */
  required: boolean;
  /** Texte d'aide affiché sous le champ de téléversement */
  help?: string;
  /**
   * Types MIME acceptés (ex: 'application/pdf,image/*').
   * Valeur vide = tous les formats.
   */
  accept?: string;
  /** Ordre d'affichage (croissant) */
  ordre: number;
}

export interface TypeOperationDef {
  /** Identifiant BD (UUID) — undefined pour les valeurs locales legacy */
  id?: string;
  /** Clé technique unique, ex: 'VENTE', 'PAIE_PERSONNEL' */
  code: string;
  /** Libellé affiché */
  libelle: string;
  /** Journal comptable par défaut : 'OD' | 'VE' | 'AC' | 'CA' | 'BQ' | 'DC' */
  journal: string;
  /** Activé/désactivé pour les sélecteurs */
  actif: boolean;
  /** Type fourni par défaut (seed) — non supprimable, juste désactivable */
  systeme: boolean;
  /** Note interne optionnelle */
  description?: string;
  /** Catégorie d'opération (paiement_simple, paiement_masse, avance…) */
  categorie?: string;
  /**
   * Sens financier (MM-10) — pilote l'apparition du type dans les hubs
   * paiement caisse (DECAISSEMENT/MIXTE) et encaissement (ENCAISSEMENT/MIXTE).
   */
  sens?: SensOperation;
  /** Ordre d'affichage */
  ordre?: number;
  /**
   * Modules SAGeCom où ce type est utilisable.
   * Tableau de codes : ['caisse', 'comptabilite', 'rh', 'achats', 'stock', …].
   * Vide ou non défini = utilisable dans tous les modules (rétrocompat).
   * La liste de référence est gérée en BD (table ecom_referentiels,
   * type='module'). Voir useReferentiel('module').
   */
  moduleCodes?: string[];
  /**
   * Format de génération automatique du numéro de demande (paramétrable).
   * Placeholders supportés : {prefix3}, {prefix4}, {prefixN}, {entiteCode2},
   * {entiteCode3}, {entiteCodeN}, {annee}, {annee2}, {mois}, {jour}, {chrono},
   * {chrono4}, {chrono5}, {chrono6}, {chronoN}.
   * Défaut si null/vide : `{prefix3}-{entiteCode2}-{annee}-{chrono6}`
   * Exemple résolu : "ORD-CI-2026-000042"
   */
  formatNumerotation?: string | null;
  /**
   * Format du numéro MÉTIER spécifique au type (en plus de `formatNumerotation`).
   * Mêmes placeholders. Si défini, chaque demande créée se voit attribuer
   * un `numeroMetier` séparé du `numeroDemande`.
   * Exemples : COURRIER → "{entiteCode2}-CE/{annee}/{chrono5}",
   *            ADHESION → "DOS-ADH/{annee}/{chrono5}".
   */
  formatNumeroMetier?: string | null;
  /**
   * Libellé d'affichage du numéro métier dans l'UI (ex: "N° courrier",
   * "N° de dossier", "N° de facture"). Affiché dans la fiche détail.
   */
  libelleNumeroMetier?: string | null;
  /**
   * Indique si une demande de ce type doit générer automatiquement
   * des écritures comptables selon le schéma comptable associé.
   * - `true`  → impact compta (vente, décaissement, paie…)
   * - `false` → workflow administratif seul (courrier, attestation…)
   */
  genereFluxComptable?: boolean;
  /**
   * MM-117h — Métadonnées libres pilotant le comportement du formulaire.
   * Conventions utilisées :
   *   - reserveAuxEmployes (bool) : le type d'opération n'est ouvert qu'aux
   *     employés de la société (filtrage côté frontend dans le catalogue).
   *   - useLocauxInternes (bool) : le formulaire doit afficher un sélecteur
   *     de LOCAL INTERNE (DR / Agence / Bâtiment / Atelier / Poste) à la
   *     place du <ClientLookupAutocomplete> pour les opérations EEC.
   *   - … (extensible librement, le backend stocke en JSONB).
   */
  metadata?: {
    reserveAuxEmployes?: boolean;
    useLocauxInternes?: boolean;
    [key: string]: any;
  } | null;
  /**
   * UUIDs des profils autorisés à gérer ce type d'opération.
   * Vide ou undefined = accessible à tous (comportement par défaut).
   * Géré via PATCH /secteurs/types-operation/:id/profils-autorises.
   */
  profilsAutorises?: string[];
  /**
   * Démarche ouverte à la saisie en ligne (portail abonné + public).
   * Seul drapeau faisant autorité pour les catalogues portail.
   * Géré via PATCH /secteurs/types-operation/:id/en-ligne.
   */
  enLigne?: boolean;
  /**
   * Pièces à fournir spécifiques à ce type d'opération (JSONB).
   * Si renseigné et non vide, remplace le template générique
   * `pieces_jointes_demande` dans le formulaire de nouvelle demande.
   */
  piecesRequises?: PieceRequise[];
}

const BASE = '/secteurs/types-operation';

// Backend renvoie `label` (anglais), frontend a historiquement utilisé `libelle`.
// On normalise dans les 2 sens.
type BackendType = {
  id: string;
  code: string;
  label: string;
  journal: string;
  actif: boolean;
  systeme?: boolean;
  description?: string;
  categorie?: string;
  sens?: SensOperation;
  ordre?: number;
  moduleCodes?: string[];
  formatNumerotation?: string | null;
  formatNumeroMetier?: string | null;
  libelleNumeroMetier?: string | null;
  genereFluxComptable?: boolean;
  metadata?: any | null;
  profilsAutorises?: string[];
  enLigne?: boolean;
  piecesRequises?: PieceRequise[];
};

const fromBackend = (t: BackendType): TypeOperationDef => ({
  id: t.id,
  code: t.code,
  libelle: t.label,
  journal: t.journal || 'OD',
  actif: !!t.actif,
  systeme: !!t.systeme,
  description: t.description,
  categorie: t.categorie,
  sens: t.sens || 'DECAISSEMENT', // MM-10 : fallback DECAISSEMENT pour rétrocompat
  ordre: t.ordre,
  // Important : sans cette ligne le frontend perd la liste sélectionnée
  // entre 2 chargements (la mise à jour des modules ne semble alors "pas marcher").
  moduleCodes: Array.isArray(t.moduleCodes) ? t.moduleCodes : [],
  formatNumerotation: t.formatNumerotation ?? null,
  formatNumeroMetier: t.formatNumeroMetier ?? null,
  libelleNumeroMetier: t.libelleNumeroMetier ?? null,
  genereFluxComptable: !!t.genereFluxComptable,
  metadata: t.metadata && typeof t.metadata === 'object' ? t.metadata : null,
  profilsAutorises: Array.isArray(t.profilsAutorises) ? t.profilsAutorises : [],
  enLigne: !!t.enLigne,
  piecesRequises: Array.isArray(t.piecesRequises) ? t.piecesRequises : [],
});

const toBackend = (t: Partial<TypeOperationDef>): Partial<BackendType> => {
  const out: any = { ...t };
  if (t.libelle !== undefined) {
    out.label = t.libelle;
    delete out.libelle;
  }
  // ne pas envoyer `id` au backend dans un body POST/PUT
  if ('id' in out) delete out.id;
  return out;
};

// ── Cache mémoire process-wide ───────────────────────────────────────────
const cache: {
  data: TypeOperationDef[] | null;
  promise: Promise<TypeOperationDef[]> | null;
  loaded: boolean;
} = { data: null, promise: null, loaded: false };

/** Force le rechargement (à appeler après mutation par exemple). */
export function invalidateTypesOperationCache(): void {
  cache.data = null;
  cache.promise = null;
  cache.loaded = false;
  // Le menu latéral lit la liste des processus, bâtie sur ces types : elle est
  // périmée dès qu'un type change (libellé, modules, activation…).
  invaliderCatalogueProcessus();
}

/**
 * Charge la liste depuis l'API (mémoïsé).
 * Mode = 'admin' inclut les types inactifs ; sinon seulement actifs.
 */
async function fetchFromApi(mode: 'all' | 'active' = 'active'): Promise<TypeOperationDef[]> {
  const url = mode === 'all' ? `${BASE}/list-all` : `${BASE}/list`;
  const r = await axiosInstance.get<BackendType[]>(url);
  return (r.data || []).map(fromBackend);
}

export const typesOperationService = {
  /**
   * Retourne la liste actuelle DEPUIS LE CACHE.
   * - Si le cache est vide, déclenche un fetch en arrière-plan et retourne [].
   * - Pour avoir les données fraîches : `await getAllAsync()` ou `useTypesOperation()`.
   */
  getAll(): TypeOperationDef[] {
    if (cache.loaded && cache.data) return cache.data;
    // Lazy : déclenche le fetch sans bloquer
    if (!cache.promise) {
      cache.promise = fetchFromApi('all').then(d => {
        cache.data = d;
        cache.loaded = true;
        return d;
      }).catch(() => {
        cache.promise = null;
        return [] as TypeOperationDef[];
      });
    }
    return cache.data || [];
  },

  /**
   * Variante async qui attend la fin du chargement.
   *
   * IMPORTANT — gestion d'erreur : si le fetch échoue (ex: 401 transitoire au
   * démarrage avant que l'intercepteur ait attaché le token, ou réseau coupé),
   * on RESET le cache pour permettre une nouvelle tentative au prochain appel.
   * Sans ce reset, la promesse rejetée resterait piégée dans `cache.promise`
   * et toutes les pages enfants verraient `types = []` jusqu'au reload complet
   * de l'onglet — ce qui fait apparaître à tort des erreurs « Type d'opération
   * COURRIER_ENTRANT introuvable » alors que la liste devrait juste être
   * rechargée.
   */
  async getAllAsync(): Promise<TypeOperationDef[]> {
    if (cache.loaded && cache.data) return cache.data;
    if (!cache.promise) {
      cache.promise = fetchFromApi('all').then(d => {
        cache.data = d;
        cache.loaded = true;
        return d;
      }).catch(err => {
        // Reset pour permettre une nouvelle tentative au prochain appel.
        cache.promise = null;
        cache.loaded = false;
        cache.data = null;
        throw err;
      });
    }
    return cache.promise;
  },

  /** Retourne uniquement les types actifs (depuis le cache). */
  getActifs(): TypeOperationDef[] {
    return this.getAll().filter(t => t.actif);
  },

  async getActifsAsync(): Promise<TypeOperationDef[]> {
    const all = await this.getAllAsync();
    return all.filter(t => t.actif);
  },

  /** Retourne le label d'un code (depuis le cache, fallback = code brut). */
  getLabel(code: string): string {
    return this.getAll().find(t => t.code === code)?.libelle ?? code;
  },

  /** Vérifie si un code existe (cache). */
  exists(code: string): boolean {
    return this.getAll().some(t => t.code === code);
  },

  // ── MUTATIONS (passent par l'API) ──────────────────────────────────────

  /** Crée ou met à jour un type. Si `id` fourni → PUT, sinon POST. */
  async upsert(type: TypeOperationDef): Promise<TypeOperationDef> {
    const body = toBackend(type);
    let saved: BackendType;
    if (type.id) {
      const r = await axiosInstance.put<BackendType>(`${BASE}/${type.id}`, body);
      saved = r.data;
    } else {
      const r = await axiosInstance.post<BackendType>(BASE, body);
      saved = r.data;
    }
    invalidateTypesOperationCache();
    return fromBackend(saved);
  },

  /** Supprime un type personnalisé (les types système renvoient une 400). */
  async remove(idOrCode: string): Promise<void> {
    // Si c'est un UUID → suppression directe ; sinon résoudre par code
    let id = idOrCode;
    if (!/^[0-9a-f]{8}-/i.test(idOrCode)) {
      const all = await this.getAllAsync();
      const found = all.find(t => t.code === idOrCode);
      if (!found?.id) {
        throw new Error(`Type d'opération "${idOrCode}" introuvable.`);
      }
      id = found.id;
    }
    await axiosInstance.delete(`${BASE}/${id}`);
    invalidateTypesOperationCache();
  },

  /** Bascule actif/inactif (utilisé pour les types système non supprimables). */
  async toggleActif(id: string): Promise<TypeOperationDef> {
    const r = await axiosInstance.post<BackendType>(`${BASE}/${id}/toggle`, {});
    invalidateTypesOperationCache();
    return fromBackend(r.data);
  },

  /**
   * Met à jour les pièces requises d'un type d'opération.
   * Endpoint : PATCH /secteurs/types-operation/:id/pieces-requises
   */
  async updatePiecesRequises(id: string, piecesRequises: PieceRequise[]): Promise<any> {
    const r = await axiosInstance.patch(`${BASE}/${id}/pieces-requises`, { piecesRequises });
    invalidateTypesOperationCache();
    return r.data;
  },

  /**
   * Met à jour la liste des profils autorisés à gérer un type d'opération.
   * Endpoint : PATCH /secteurs/types-operation/:id/profils-autorises
   */
  async updateProfilsAutorises(id: string, profilsAutorises: string[]): Promise<any> {
    const r = await axiosInstance.patch(`${BASE}/${id}/profils-autorises`, { profilsAutorises });
    invalidateTypesOperationCache();
    return r.data;
  },

  /**
   * Publie (ou retire) ce type dans les portails en ligne.
   *
   * `enLigne` est le seul drapeau qui fait autorité : catalogues portail et
   * endpoints de soumission publique s'y réfèrent. Le backend ajoute au
   * passage « public » aux profils autorisés, les deux étant exigés
   * conjointement pour un dépôt public.
   */
  async updateEnLigne(id: string, enLigne: boolean): Promise<any> {
    const r = await axiosInstance.patch(`${BASE}/${id}/en-ligne`, { enLigne });
    invalidateTypesOperationCache();
    return r.data;
  },

  /**
   * Duplique un type existant. Le backend hérite automatiquement de toutes
   * les propriétés (libellé, journal, sens, natures, modules…) et marque
   * la copie comme `systeme=false` (donc supprimable).
   */
  async duplicate(id: string, dto: { code: string; label?: string }): Promise<TypeOperationDef> {
    const body: any = { code: dto.code.trim().toUpperCase().replace(/\s+/g, '_') };
    if (dto.label) body.label = dto.label;
    const r = await axiosInstance.post<BackendType>(`${BASE}/${id}/duplicate`, body);
    invalidateTypesOperationCache();
    return fromBackend(r.data);
  },

  // ── DEPRECATED (pour rétrocompat — ne fait plus rien d'utile) ─────────

  /**
   * @deprecated Le seed est désormais côté backend via OnModuleInit.
   * Cette méthode reste pour rétrocompatibilité mais ne fait plus rien.
   */
  reset(): void {
    invalidateTypesOperationCache();
  },

  /**
   * @deprecated Save direct localStorage retiré — utilisez `upsert()`
   * type par type pour passer par le backend.
   */
  save(_types: TypeOperationDef[]): void {
    console.warn('[typesOperationService.save] deprecated — utilisez upsert() unitaire.');
  },
};

/**
 * @deprecated Conservé pour rétrocompat des imports historiques. La source
 * de vérité est désormais le backend (table ecom_types_operation, seed
 * automatique au boot). Migration complète : utilisez `useTypesOperation()`.
 */
export const TYPES_OPERATION_DEFAUT: TypeOperationDef[] = [];
